import { useMutation } from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'
import React, { useCallback, useMemo, useState } from 'react'
import { FileTypeEnum } from 'utils'

import useFilePicker from 'components/finder-v2/useFilePicker'
import { useUppy } from 'components/finder-v2/useUppy'
import Form from 'components/formKit/Form'
import FormField from 'components/formKit/FormField'
import FormButtonSubmit from 'components/formKit/components/FormButtonSubmit'
import FieldInput from 'components/formKit/fields/FieldInput'
import FieldSelect from 'components/formKit/fields/FieldSelect'
import { genFieldUnsafe } from 'components/formKit/genConfig'
import { useFormValues } from 'components/formKit/hooks'
import Button from 'components/uiKit/Button'
import Checkbox from 'components/uiKit/Checkbox'
import { KitSize } from 'components/uiKit/KitTypes'
import type { MCWithParams } from 'components/uiKit/Modal'
import Modal, { ModalBody, ModalFooter } from 'components/uiKit/Modal'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { FileMetaFor, Locale, NavigationFooterMode } from 'gql/__generated__/graphql'
import { useGetAllBrandSelectItems } from 'gql/brands/apollo'
import { useFilesSubscription } from 'gql/files/apollo'
import { fileMetaAllQuery } from 'gql/files/gql/queries'
import { projectGroupAll } from 'gql/projectGroups/gql/queries'
import { useValidateProjectForRiseLazy } from 'gql/projects/apollo'
import { importProjectFromRise } from 'gql/projects/gql/mutations'
import { t } from 'services/Translation'
import type { UppySucssesfull } from 'store/models'
import { getEnumOption } from 'utils/enum'

const STORAGE_KEY_PREFIX = 'riseImportSettings_'

const getStorageKey = (companyId: string) => `${STORAGE_KEY_PREFIX}${companyId}`

interface IRiseImportSavedSettings {
  brandId?: string
  targetFolderId?: string
  templateProjectId?: string
}

const getSavedSettings = (companyId: string): IRiseImportSavedSettings => {
  try {
    const saved = localStorage.getItem(getStorageKey(companyId))
    return saved ? JSON.parse(saved) : {}
  } catch {
    return {}
  }
}

const saveRiseSettings = (companyId: string, settings: Partial<IRiseImportSavedSettings>) => {
  try {
    const current = getSavedSettings(companyId)
    localStorage.setItem(getStorageKey(companyId), JSON.stringify({ ...current, ...settings }))
  } catch {
    // ignore
  }
}

interface IRiseImportParams {
  categoryId: string
  companyId: string
  fileMetaIds?: string[]
}

type RiseImportFormData = {
  locale: Locale
  templateProjectId?: string
  brandId?: string
  targetFolderId?: string
  navigationFooter: NavigationFooterMode
}

const FILE_PICKER_OPTIONS = {
  fileTypes: [FileTypeEnum.TEXT],
  createdFor: FileMetaFor.owner,
  multiple: false,
  name: 'risePackage',
}

const LOCALE_OPTIONS = [
  getEnumOption('Locale', Locale.EN),
  getEnumOption('Locale', Locale.RU),
  getEnumOption('Locale', Locale.ES),
  getEnumOption('Locale', Locale.FR),
  getEnumOption('Locale', Locale.IT),
  getEnumOption('Locale', Locale.DE),
  getEnumOption('Locale', Locale.PT),
  getEnumOption('Locale', Locale.PL),
  getEnumOption('Locale', Locale.TR),
  getEnumOption('Locale', Locale.UA),
  getEnumOption('Locale', Locale.CN),
  getEnumOption('Locale', Locale.JA),
  getEnumOption('Locale', Locale.SK),
  getEnumOption('Locale', Locale.RO),
]

const localeField = genFieldUnsafe({
  name: 'locale',
  as: FieldSelect<string>,
  label: t('modal.riseImport.locale'),
  props: {
    options: LOCALE_OPTIONS,
  },
})

const folderField = genFieldUnsafe({
  name: 'targetFolderId',
  as: FieldInput,
  label: t('modal.projectImport.folderLabel'),
  props: {
    placeholder: t('modal.projectImport.folderPlaceholder'),
  },
})

const NAVIGATION_FOOTER_OPTIONS = [
  getEnumOption('NavigationFooterMode', NavigationFooterMode.NONE),
  getEnumOption('NavigationFooterMode', NavigationFooterMode.SINGLE_BUTTON),
  getEnumOption('NavigationFooterMode', NavigationFooterMode.MULTI_BUTTON),
]

const navigationFooterField = genFieldUnsafe({
  name: 'navigationFooter',
  as: FieldSelect<string>,
  label: t('modal.riseImport.navigationFooter'),
  props: {
    options: NAVIGATION_FOOTER_OPTIONS,
  },
})

const templateProjectField = genFieldUnsafe({
  name: 'templateProjectId',
  as: FieldInput,
  label: t('modal.riseImport.templateProjectId'),
  props: {
    placeholder: t('modal.riseImport.templateProjectIdPlaceholder'),
  },
})

const templateRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
}

const templateFieldStyle: React.CSSProperties = {
  flex: 1,
}

interface IRiseBlockValidationDTO {
  blockType: string
  status: string
  targets: { name: string; status: string }[]
}

const buildValidationMarkdown = (projectId: string, blocks: IRiseBlockValidationDTO[]): string => {
  const totalCount = blocks.length
  const okCount = blocks.filter((b) => b.status === 'OK').length
  const missingCount = blocks.filter((b) => b.status === 'MISSING').length
  const noTemplateCount = blocks.filter((b) => b.status === 'NO_TEMPLATE').length

  const lines: string[] = []
  lines.push('# Rise template validation')
  lines.push('')
  lines.push(`Project ID: \`${projectId}\``)
  lines.push(`Generated: ${new Date().toISOString()}`)
  lines.push('')
  lines.push('## Summary')
  lines.push('')
  lines.push(`- Total: ${totalCount}`)
  lines.push(`- OK: ${okCount}`)
  lines.push(`- Missing: ${missingCount}`)
  lines.push(`- No template: ${noTemplateCount}`)
  lines.push('')
  lines.push('## Blocks')

  for (const block of blocks) {
    lines.push('')
    lines.push(`### ${block.blockType} — ${block.status}`)
    if (block.status === 'NO_TEMPLATE') {
      lines.push('')
      lines.push('_No template block in project_')
      continue
    }
    for (const target of block.targets) {
      lines.push(`- ${target.name}: ${target.status}`)
    }
  }

  return lines.join('\n')
}

const downloadValidationReport = (projectId: string, content: string) => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `rise-validation-${projectId}-${timestamp}.md`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const ValidateButton: React.FC = () => {
  const values = useFormValues<RiseImportFormData>()
  const [validate, { loading }] = useValidateProjectForRiseLazy()

  const onClick = useCallback(async () => {
    const projectId = values?.templateProjectId?.trim()
    if (!projectId) {
      return
    }
    const result = await validate({ variables: { projectId } })
    if (result.error) {
      notify({
        type: NotificationType.error,
        message: t('modal.riseImport.error.validationFailed'),
      })
      return
    }
    const blocks = result.data?.validateProjectForRise?.blocks
    if (!blocks) {
      return
    }
    const markdown = buildValidationMarkdown(projectId, blocks)
    downloadValidationReport(projectId, markdown)
  }, [values?.templateProjectId, validate])

  return (
    <Button
      disabled={!values?.templateProjectId?.trim() || loading}
      loading={loading}
      name='validateRiseTemplate'
      onClick={onClick}
      size={KitSize.S}
      styleType='secondary'
    >
      {t('modal.riseImport.validate')}
    </Button>
  )
}

const RiseImport: MCWithParams<IRiseImportParams, boolean> = ({ onClose, params }) => {
  const { categoryId, companyId } = params

  const [isLoading, setIsLoading] = useState(false)
  const [uploadedFileMetaId, setUploadedFileMetaId] = useState<string | null>(null)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [comments, setComments] = useState(true)

  const { data: brandsData } = useGetAllBrandSelectItems({ companyId })

  const brandsOptions = useMemo(
    () =>
      brandsData?.data?.brands?.map((brand) => ({
        value: brand.id,
        label: brand.name,
      })) || [],
    [brandsData],
  )

  const brandField = useMemo(
    () =>
      genFieldUnsafe({
        name: 'brandId',
        as: FieldSelect<string>,
        label: t('modal.riseImport.brand'),
        props: {
          options: brandsOptions,
          placeholder: t('modal.riseImport.brandPlaceholder'),
          isSearch: true,
        },
      }),
    [brandsOptions],
  )

  const hasPreselectedFiles = params.fileMetaIds && params.fileMetaIds.length > 0
  const savedSettings = useMemo(() => getSavedSettings(companyId), [companyId])
  const defaultBrandId = brandsOptions.length > 0 ? brandsOptions[0].value : undefined

  const initialValues: RiseImportFormData = useMemo(
    () => ({
      locale: Locale.EN,
      templateProjectId: savedSettings.templateProjectId || '',
      targetFolderId: hasPreselectedFiles ? savedSettings.targetFolderId || '' : categoryId || '',
      brandId: savedSettings.brandId || defaultBrandId,
      navigationFooter: NavigationFooterMode.NONE,
    }),
    [hasPreselectedFiles, categoryId, savedSettings, defaultBrandId],
  )

  const [importRise] = useMutation(importProjectFromRise, {
    refetchQueries: [
      String(getOperationName(projectGroupAll)),
      String(getOperationName(fileMetaAllQuery)),
    ],
  })

  const uppy = useUppy((_uppy, _result, data) =>
    data.forEach((item) =>
      item.successful.forEach((file: UppySucssesfull<{ data?: { name?: string } }>) => {
        if (file.meta.fieldName === FILE_PICKER_OPTIONS.name) {
          setUploadedFileMetaId(file.meta.fileMetaId)
          setUploadedFileName(file.data?.name || 'package.zip')
        }
      }),
    ),
  )

  useFilesSubscription({ companyId, parentId: null })

  const { onClickUploadFile } = useFilePicker({
    uppy,
    ...FILE_PICKER_OPTIONS,
    params: { companyId },
  })

  const importSingleFile = useCallback(
    async (fileMetaId: string, values: RiseImportFormData) => {
      const folderId = values.targetFolderId?.trim()
      const targetProjectGroupId = folderId && folderId !== 'root' ? folderId : undefined
      const result = await importRise({
        variables: {
          data: {
            fileMetaId,
            companyId,
            targetProjectGroupId,
            locale: values.locale,
            templateProjectId: values.templateProjectId || undefined,
            brandId: values.brandId || undefined,
            comments,
            navigationFooter: values.navigationFooter,
          },
        },
      })
      return result.data?.importProjectFromRise
    },
    [importRise, companyId, comments],
  )

  const handleSubmit = useCallback(
    async (values: RiseImportFormData) => {
      saveRiseSettings(companyId, {
        brandId: values.brandId,
        targetFolderId: values.targetFolderId,
        templateProjectId: values.templateProjectId,
      })

      const notifyValidation = (result: {
        missedBlocks?: string[] | null
        validationErrors?: string[] | null
      }) => {
        const warnings = [
          ...(result.missedBlocks?.map((b) => `Missing template: ${b}`) || []),
          ...(result.validationErrors || []),
        ]
        if (warnings.length) {
          notify({
            type: NotificationType.warning,
            message: warnings.join('\n'),
          })
        }
      }

      if (hasPreselectedFiles) {
        setIsLoading(true)
        let successCount = 0
        for (const fileMetaId of params.fileMetaIds!) {
          try {
            const result = await importSingleFile(fileMetaId, values)
            if (result) {
              notifyValidation(result)
            }
            successCount++
          } catch (error) {
            notify({
              type: NotificationType.error,
              message: t('modal.riseImport.error.importFailed', {
                message: (error as Error).message,
              }),
            })
          }
        }
        if (successCount > 0) {
          notify({
            type: NotificationType.success,
            message: t('modal.riseImport.success'),
          })
        }
        setIsLoading(false)
        onClose(successCount > 0)
        return
      }

      if (!uploadedFileMetaId) {
        notify({
          type: NotificationType.error,
          message: t('modal.riseImport.error.fileRequired'),
        })
        return
      }

      setIsLoading(true)

      try {
        const result = await importSingleFile(uploadedFileMetaId, values)
        if (result?.project) {
          notifyValidation(result)
          notify({
            type: NotificationType.success,
            message: t('modal.riseImport.success'),
          })
          onClose(true)
        }
      } catch (error) {
        notify({
          type: NotificationType.error,
          message: `Rise import failed: ${(error as Error).message}`,
        })
      } finally {
        setIsLoading(false)
      }
    },
    [
      hasPreselectedFiles,
      uploadedFileMetaId,
      importSingleFile,
      onClose,
      params.fileMetaIds,
      companyId,
    ],
  )

  const canSubmit = hasPreselectedFiles || uploadedFileMetaId !== null

  return (
    <Form initialValues={initialValues} name='riseImport' onSubmit={handleSubmit}>
      <Modal name='importRise' title={t('modal.riseImport.title')} width={600}>
        <ModalBody sizeAutoCapable>
          <div>
            {hasPreselectedFiles ? (
              <p>
                {t('modal.projectImport.filesSelected', {
                  count: params.fileMetaIds!.length,
                })}
              </p>
            ) : (
              <div>
                {uploadedFileName ? (
                  <div>
                    <span>{uploadedFileName}</span>{' '}
                    <Button
                      name='changeRiseFile'
                      onClick={onClickUploadFile}
                      size={KitSize.S}
                      styleType='ghost'
                    >
                      {t('uiKit.button.change')}
                    </Button>
                  </div>
                ) : (
                  <Button
                    name='selectRiseFile'
                    onClick={onClickUploadFile}
                    size={KitSize.S}
                    styleType='secondary'
                  >
                    {t('modal.riseImport.selectFile')}
                  </Button>
                )}
              </div>
            )}

            {hasPreselectedFiles ? (
              <FormField config={folderField} />
            ) : (
              categoryId &&
              categoryId !== 'root' && (
                <p>
                  {t('modal.projectImport.folderLabel')}: {categoryId}
                </p>
              )
            )}
            <FormField config={localeField} />
            <FormField config={brandField} />
            <FormField config={navigationFooterField} />
            <div style={templateRowStyle}>
              <div style={templateFieldStyle}>
                <FormField config={templateProjectField} />
              </div>
              <ValidateButton />
            </div>

            <Checkbox name='comments' onChange={setComments} value={comments}>
              {t('modal.riseImport.comments')}
            </Checkbox>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button name='cancelRiseImport' onClick={() => onClose()} styleType='secondary'>
            {t('uiKit.button.cancel')}
          </Button>
          <FormButtonSubmit
            disabled={isLoading || !canSubmit}
            label={t('modal.riseImport.submit')}
            loading={isLoading}
            name='submitRiseImport'
          />
        </ModalFooter>
      </Modal>
    </Form>
  )
}

export default RiseImport
